
import { computed, defineComponent, markRaw, PropType } from 'vue';
import { useStore } from 'vuex';
import { Actions, Getters, store, Mutations, userSettingsStore, formFieldsStore, formFieldsStoreV36 } from '@/store';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import { FieldNames } from '@/constants/FieldNames';
import GridRowCellData, { getRowVisibilityState, normalizeRowVisibilityKey } from '@/models/grid/GridRowCellData';
import GridColumnUtils from '@/utils/GridColumnUtils';
import utils from '@/utils/QSUtils';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import PopoverService from '@/modules/popovers/services/popoverService';
import _ from 'lodash';
import BootstrapHelper from '@/utils/bootstrapHelpers';
import GuideManager from '@/manager/GuideManager';
import ModalConfirmation from '@/components/modals/Confirmation.vue';
import ModalSuggestFormattingRule from '@/components/3.6/modals/SuggestFormattingRule.vue';
import ITransferComparisonValue from '@/models/TransferComparisonValue';
import IFormFieldRule from '@/models/form-field/IFormFieldRule';
import FieldRulesManager from '@/manager/3.6/FieldRulesManager';
import Notes from '@/modules/notes/components/notes.vue';
import NotesService from '@/modules/notes/services/scripts';
import { newUpsertCompDataRequestByFieldName } from '@/store/3.6/models/IUpsertCompDataRequest';
import FormsManager from '@/manager/3.6/FormsManager';
import IFormFieldsAdditionalDetails from '@/models/forms/IFormFieldsAdditionalDetails';
import IFieldSubjectCompRelationship from '@/models/forms/IFieldSubjectCompRelationship';
import { VisibilityGrid } from '@/constants/VisibilityGrid';

export default defineComponent({
    components: {
        ModalConfirmation,
        ModalSuggestFormattingRule,
        Notes,
    },
    emits: ['toggle-row-visibility'],
    props: {
        cellData: {
            type: Object as PropType<GridRowCellData>,
            required: true,
        },
        fieldId: {
            type: Number,
            required: true,
        },
        fieldName: {
            type: String,
            required: true,
        },
        fieldFriendlyName: {
            type: String,
            required: true,
        },
        photoRow: {
            type: Boolean,
            required: true,
        },
        isRowVisible: {
            type: Boolean,
            required: true,
        },
        isSubject: {
            type: Boolean,
            required: true,
        },
    },
    data(props) {
        return {
            NotesPopover: null,
            ConcatenatedValuePopover: null,
            cellDataClone: props.cellData,
            isEditing: false,
            editValue: '',
            cellId: utils.ranStr(10, 'lower'),
            timer: 0,
            modalConfirmation: {
                show: false,
                component: markRaw(ModalConfirmation),
                message: '',
                size: '',
                buttons: [] as any,
                title: '',
            },
            modalSuggestFormattingRule: {
                show: false,
                component: markRaw(ModalSuggestFormattingRule),
                originalValue: '',
                newValue: '',
            },
        };
    },
    setup() {
        const store = useStore();

        const dataPreferenceOverrides = {} as any;

        return { store, dataPreferenceOverrides };
    },
    watch: {
        cellData(cellData) {
            this.cellDataClone = cellData;
        },
    },
    computed: {
        isFieldData(): boolean {
            return this.cellData.initials === SourceDataInitials.FieldData;
        },
            isSubjectProperty(): boolean {
                return this.cellData.initials === SourceDataInitials.SubjectProperty;
            },
        fieldRowVisibilityState(): VisibilityGrid | 'default' {
            const storeState = getRowVisibilityState(this.fieldName, formFieldsStoreV36.RowVisibilityGrid);
            return storeState;
        },
        showRowVisibilityIcon(): boolean {
            return !this.isSubject && this.fieldRowVisibilityState !== VisibilityGrid.AlwaysOn;
        },
        rowVisibilityIconClass(): string {
            switch (this.fieldRowVisibilityState) {
                case VisibilityGrid.Hidden:
                    return 'row-icon-nonvisible';
                case VisibilityGrid.Visible:
                    return 'row-icon-visible';
                default:
                    // No server-forced state, so reflect the locally toggled visibility.
                    return this.isRowVisible ? 'row-icon-visible' : 'row-icon-nonvisible';
            }
        },
        isRoomLevelField(): boolean {
            return String(this.fieldName || '').includes('ROOM_LEVEL');
        },
        isTransferData(): boolean {
            return this.cellData.initials === SourceDataInitials.TransferData;
        },
        isCascadeData(): boolean {
            return this.cellData.initials === SourceDataInitials.CascadeData;
        },
        isPhotoRow(): boolean {
            // Don't rely solely on the externally-passed photoRow prop - fieldName is always available here.
            return this.photoRow || this.fieldName === FieldNames.CompPhoto;
        },
    },
    methods: {
        toggleRowVisibility() {
            this.$emit('toggle-row-visibility', {
                groupKey: normalizeRowVisibilityKey(this.fieldName),
                isVisible: !this.isRowVisible,
            });
        },
        showAdditionalFields() {
            (this as any).eventBus.emit('launchAdditionalFieldsModal');
        },
        launchDataPreferenceSettingsModal() {
            (this as any).eventBus.emit('launchDataPreferenceSettingsModal', {
                defaultTabIdx: 0,
                fieldId: this.fieldId,
            });
        },
        launchFieldRulesModal() {
            (this as any).eventBus.emit('launchFieldRulesModal', {
                defaultTabIdx: 1,
                fieldId: this.fieldId,
            });
        },
        showConcatenatedValueIcon(fieldName: string): boolean {
            if (this.cellDataClone.isFakeData || this.cellDataClone.value === '') {
                return false;
            }

            // Always hidden regardless of child field count.
            if (GridColumnUtils.getNormalizedFieldName(fieldName).baseFieldname === 'HORESPMAINT_FLATTENED') {
                return false;
            }

            // A single child field is just a rename of the parent value itself - not worth a popup.
            return this.getVisibleConcatenatedChildFieldNames(fieldName).length > 1;
        },
        getVisibleConcatenatedChildFieldNames(fieldName: string): string[] {
            const childFieldNames = this.isSubjectProperty
                ? this.getSubjectParentChildFields(fieldName)
                : GridColumnUtils.getParentChildFields(fieldName);
            const baseFieldName = GridColumnUtils.getNormalizedFieldName(fieldName).baseFieldname.replace(/_FLATTENED$/, '');
            if (baseFieldName !== 'SIT_FEAT_SITCHAR_DTL') {
                return childFieldNames;
            }

            const siteFeatureType = String(this.cellDataClone.value || '')
                .split('|')[0]
                .trim()
                .toLowerCase();
            if (siteFeatureType === 'drainage') {
                return childFieldNames.filter((childFieldName: string) =>
                    childFieldName === 'PU_SITE_FEAT_SITCHAR' ||
                    childFieldName === 'PU_SITE_FEAT_SITCHAR_DRAIN_RSN'
                );
            }
            if (siteFeatureType === 'topography') {
                return childFieldNames.filter((childFieldName: string) =>
                    childFieldName === 'PU_SITE_FEAT_SITCHAR' ||
                    childFieldName === 'PU_SITE_FEAT_SITCHAR_TOP_TYPE'
                );
            }

            return [];
        },
        showSourceSkeletonLoader(): boolean {
            if (!this.cellDataClone.isFakeData || !this.cellDataClone.isPendingSourceData) {
                return false;
            }

            const sourceInitial = String(this.cellDataClone.sourceInitial || '').toLowerCase();
            return sourceInitial === SourceDataInitials.Database ||
                sourceInitial === SourceDataInitials.Mls ||
                sourceInitial === SourceDataInitials.SmartExchange ||
                sourceInitial === SourceDataInitials.PublicRecords;
        },
        photoClick() {
            (this as any).eventBus.emit('launchImageViewerModal', {
                imagePath: this.cellData.value,
            });
        },
        hasCompPhoto(): boolean {
            if (this.cellData.value && (this.cellData.value.includes('http') || this.cellData.value.includes('data:image'))) {
                return true;
            } else if(theLocalStorageUtils.hasKey('load-fake-data') && theLocalStorageUtils.get('load-fake-data') === 'true') {
                // We reference a local image for testing purposes because all images are in AWS and the url expires after a certain amount of time.
                return true;
            }
            return false;
        },
        async startEditingValue() {
            // Disabled for 3.6 until we know how to handle.
            
            // if (this.isEditing) {
            //     return;
            // }

            // this.isEditing = true;
            // this.editValue = this.cellDataClone.value;

            // await utils.sleep(100);

            // const inputEl = document.querySelector<HTMLInputElement>(`.ut-edit[data-id="${this.cellId}"]`);
            // inputEl?.focus();
            // inputEl?.setSelectionRange(this.cellDataClone.value.length, this.cellDataClone.value.length);
        },
        async doneEditingValue(value: string) {
            if (!this.isEditing) {
                return;
            }

            this.isEditing = false;

            const sourceInitial = SourceDataInitials.UserOverride;
            const originalValue = this.cellDataClone.value.trim();
            value = value.trim();

            // console.log(`GridRowCell.doneEditingValue(), Value was[${originalValue}] and is[${value}] on [${this.fieldId},${this.fieldName}].`);

            if (originalValue !== value) {
                const fieldRule = await this.runRulesChecker(value);
                if (fieldRule.fieldRulesApplied) {
                    value = fieldRule.value;
                }
                this.updateActiveCompSourceData(value, sourceInitial, fieldRule.fieldRulesApplied ?? false, undefined);

                const suggestRule = this.cellDataClone.suggestFieldRule;
                if (suggestRule) {
                    const comparison: ITransferComparisonValue = GridColumnUtils.transferValueComparison(suggestRule, originalValue, value);
                    if (!utils.isObjectEmpty(comparison)) {
                        this.modalSuggestFormattingRule.show = true;
                        this.modalSuggestFormattingRule.originalValue = comparison?.changedText?.originalValue || originalValue;
                        this.modalSuggestFormattingRule.newValue = comparison?.changedText?.newValue || value;
                    }
                }
            }
        },
        getInitialName(initial: string) {
            const initialDetails = utils.initialDetails(initial.toLowerCase());
            return initialDetails.title;
        },
        async runRulesChecker(value: string) {
            // Run Rules Checker
            const fieldRule: IFormFieldRule = {
                fieldId: this.fieldId,
                value: value,
            };

            // console.log(`GridRowCell.runRulesChecker(), Begin run field rule on [${this.fieldId},${this.fieldName}] on active comp.`);

            const fieldRules = await FieldRulesManager.runFieldRule(fieldRule);
            if (fieldRules && fieldRules.length > 0) {
                return fieldRules[0];
            }

            fieldRule.fieldRulesApplied = false;
            return fieldRule;
        },
        async updateActiveCompSourceData(value: string, sourceInitial: string, fieldRulesApplied: boolean, sourceInitialIdx: number | undefined) {
            const comp = computed(() => store.getters[Getters.CompAddressesV36.ActiveComp]);

            const request = newUpsertCompDataRequestByFieldName(comp.value, this.fieldName);
            request.value = value;
            request.sourceInitial = sourceInitial.toLowerCase();
            request.fieldId = this.fieldId;
            request.fieldRulesApplied = fieldRulesApplied;
            request.sourceInitialIdx = sourceInitialIdx;
            request.description = 'GridRowCell.updateActiveCompSourceData()';
            store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldName, request);
        },
        async selectedValue(value: string, sourceInitial: string, field: string, fieldFriendlyName: string, sourceInitialIdx: number | undefined) {

            const suggest = computed(() => userSettingsStore.SuggestDataPreferenceUpdate);
            const fieldAndSource = field + sourceInitial.toLowerCase();

            const sourceInitialDetails = utils.initialDetails(sourceInitial);
            if (this.saveClick(fieldAndSource) === 3 && suggest.value) this.updatePrefsModal(fieldFriendlyName, sourceInitialDetails);
            GuideManager.guideCompleted(4);

            const comp = computed(() => store.getters[Getters.CompAddressesV36.ActiveComp]);
            const currentSourceData = comp.value?.sourceData?.find((item: any) => item?.fieldName === this.fieldName);
            const currentInitial = String(currentSourceData?.sourceInitial || '').toLowerCase();
            const currentSourceInitialIdx =
                currentSourceData?.sourceInitialIdx === undefined || currentSourceData?.sourceInitialIdx === null
                    ? undefined
                    : Number(currentSourceData.sourceInitialIdx);
            const clickedSourceInitialIdx =
                sourceInitialIdx === undefined || sourceInitialIdx === null
                    ? -2
                    : Number(sourceInitialIdx);
            const isSameSelection =
                currentInitial === String(sourceInitial || '').toLowerCase() &&
                currentSourceInitialIdx === clickedSourceInitialIdx;

            // Only toggle off when clicking the exact active selection.
            // If the user clicks a different column/value, always switch to that selection.
            if (this.cellDataClone.dataSelected && isSameSelection) {
                value = '';
                sourceInitial = SourceDataInitials.UserOverride;
                sourceInitialIdx = undefined;
            } else if (sourceInitialIdx === undefined) {
                // Manual clicks on the main source columns need to stay cell-specific.
                // Use a sentinel so the grid can distinguish them from the bulk "Use all data" action.
                sourceInitialIdx = -2;
            }

            // Skip field rule check if no rules exist.
            const fieldData = await FormsManager.getFieldDataById(this.fieldId);

            let fieldRulesApplied = false;

            if (fieldData?.hasFormattingRules) {
                const fieldRule = await this.runRulesChecker(value);
                if (fieldRule.fieldRulesApplied) {
                    value = fieldRule.value;
                }

                fieldRulesApplied = fieldRule.fieldRulesApplied ?? false;
            }

            this.updateActiveCompSourceData(value, sourceInitial, fieldRulesApplied, sourceInitialIdx);
        },
        saveClick(fieldAndSource: string): number {
            if (theLocalStorageUtils.hasKey('dataPreferenceOverrides')) {
                this.dataPreferenceOverrides = theLocalStorageUtils.get('dataPreferenceOverrides');
            } else {
                theLocalStorageUtils.save('dataPreferenceOverrides', '');
            }
            if (utils.isKeyInObject(this.dataPreferenceOverrides, fieldAndSource)) {
                this.dataPreferenceOverrides[fieldAndSource] = Number(this.dataPreferenceOverrides[fieldAndSource]) + 1;
            } else {
                this.dataPreferenceOverrides[fieldAndSource] = 1;
            }

            theLocalStorageUtils.save('dataPreferenceOverrides', JSON.stringify(this.dataPreferenceOverrides));
            return this.dataPreferenceOverrides[fieldAndSource];
        },
        updatePrefsModal(fieldFriendlyName: string, sourceInitialDetails: any) {
            this.modalConfirmation.show = true;
            this.modalConfirmation.title = `Update Data Preference for ${fieldFriendlyName}`;
            this.modalConfirmation.message = `<div>
                                                <p>You changed <b>${fieldFriendlyName}</b> to use data from <b>${sourceInitialDetails.title}</b> three times. Would you like to always use ${sourceInitialDetails.title} data for this field, if available?</p>
                                                <p>QuickSource saves you the most time when you set your data preferences for each field. You can do this in the Data Preferences column or by clicking <i>Review Data Preferences</i> below.</p>
                                                <p><label><input type="checkbox" name="doNotPromptDataPreference" class="me-2">Do not suggest changes to my Data Preferences</label></p>
                                              </div>`;

            this.modalConfirmation.buttons = [
                {
                    label: 'Review Data Preferences',
                    css: 'btn-outline-secondary',
                    callback: () => {
                        this.updateDoNotShowSetting();
                        this.launchDataPreferenceSettingsModal();
                    },
                },
                {
                    label: 'Save',
                    css: 'btn-primary',
                    callback: async () => {
                        const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode);
                        const result = await FormsManager.getFieldDataById(this.fieldId);
                        const obj = GridColumnUtils.parseFlattenedDataPriorities(result?.flattenedDataPriorities ?? '');
                        const idx = obj.findIndex((x: { initials: { toString: () => string } }) => x.initials === sourceInitialDetails.initials);
                        if (idx !== -1) {
                            obj[idx].order = 0;
                        }

                        await formFieldsStore.saveDataPreferenceSourceOrder({
                            fieldId: this.fieldId,
                            dataPreferenceSourceOrder: obj,
                            formCode: formCode,
                        });

                        this.updateDoNotShowSetting();
                    },
                },
                {
                    label: 'Close',
                    css: 'btn-outline-secondary',
                    callback: () => {
                        this.updateDoNotShowSetting();
                    },
                },
            ];
        },
        updateDoNotShowSetting() {
            const doNotPromptDataPreference = document.querySelector('input[name=doNotPromptDataPreference]') as HTMLInputElement;
            userSettingsStore.setSuggestDataPreferenceUpdate({ SuggestDataPreferenceUpdate: !doNotPromptDataPreference.checked });
        },
        getFieldLabelName(fieldName: string) {
            return `CM_OTHR${fieldName.replace(/[^0-9]/g, '')}_TEXT`;
        },
        additionalFieldName(event: any, fieldName: string) {
            clearTimeout(this.timer);
            this.timer = window.setTimeout(() => {
                this.store.dispatch(Actions.CompAddressesV36.UpdateAdditionalField, {
                    fieldName: fieldName,
                    value: event.target.value,
                });
            }, 500);
        },
        getValue(value: string) {
            const getterComps = store.getters[Getters.CompAddressesV36.Comps];
            const fieldNameLabel = this.getFieldLabelName(value);
            const itemIdx = getterComps[0].sourceData.findIndex((x: { fieldName: { toString: () => any } }) => x.fieldName === fieldNameLabel);
            let retVal = '';
            if (itemIdx !== -1) {
                retVal = getterComps[0].sourceData[itemIdx].value;
            }
            return retVal;
        },
        showNotesPopover(event: any, fieldName: string, friendlyFieldName: string, notes: string) {
            if (this.NotesPopover) {
                PopoverService.hide(this.NotesPopover);
            }

            PopoverService.hideAll();
            this.NotesPopover = null;

            // Popover
            const options = {
                html: true,
                sanitize: false,
                content: this.generateNotesHTML(friendlyFieldName, notes),
                placement: 'right',
                customClass: 'notes-popover',
            };

            this.NotesPopover = PopoverService.initialize(event.target, options);
            PopoverService.open(this.NotesPopover);
            const el = PopoverService.getPopoverElement(this.NotesPopover);

            // On open listeners
            PopoverService.onOpen(this.NotesPopover, async () => {
                BootstrapHelper.tooltipInitialize();

                // Save button
                el.getElementsByClassName('notes-save')[0].addEventListener('click', () => {
                    const value = el.getElementsByClassName('notes-area')[0].value;

                    if (value.length > 256) {
                        return;
                    }

                    store.dispatch(Actions.CompAddressesV36.UpdateInsertNotes, { fieldName: fieldName, value: value });

                    PopoverService.hide(this.NotesPopover);
                    this.NotesPopover = null;
                });

                // Close button
                el.getElementsByClassName('notes-close')[0].addEventListener('click', () => {
                    PopoverService.hide(this.NotesPopover);
                    this.NotesPopover = null;
                });

                // Textarea
                el.getElementsByClassName('notes-area')[0].addEventListener('keyup', (e: any) => {
                    const notesDetails = NotesService.formatNotes({
                        notesMultiLines: false,
                        notesLimit: 256,
                        notesValue: e.target.value,
                    });

                    const notesContainer = e.target.closest('.notes-container');
                    const count = notesContainer.getElementsByClassName('notes-count')[0];
                    const countError = notesContainer.getElementsByClassName('notes-error')[0];
                    const textarea = notesContainer.getElementsByClassName('notes-area')[0];

                    if (notesDetails.notesError) {
                        count.classList.add('text-red');
                        countError.removeAttribute('hidden');
                    } else {
                        count.classList.remove('text-red');
                        countError.setAttribute('hidden', 'true');
                    }

                    textarea.value = notesDetails.notesValue;
                    count.innerHTML = notesDetails.notesValue.length;
                });
            });
        },
        generateNotesHTML(friendlyFieldName: string, notes: string) {
            let html = `<div class="notes-container">`;

            // Header
            html += `<div class="pop-header mb-2">
                            <div class="fw-bold">${friendlyFieldName} Notes</div>
                            <div class="popover-actions">
                                <span class="hover" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Notes will be saved to the report and can be accessed within E&O." aria-label="Notes will be saved to the report and can be accessed within E&O."><i class="fas fa-info-circle"></i></span>
                            </div>
                       </div>`;

            // Body
            html += `<div class="pop-body">`;
            html += `<textarea class="notes-area">${notes}</textarea>`;

            html += `<div class="container p-0">
                      <div class="row">
                      	<div class="col align-self-center">
                    		<div class="notes-counts fw-bold">
                    			<span class="notes-count">${notes.length}</span> / <span class="notes-limit">256</span>
                    			<span hidden class="notes-error text-red" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="You have exceeded the 256 character limit for notes." aria-label="You have exceeded the 256 character limit for notes."><i class="fas fa-exclamation-triangle"></i></span>
                    		</div>
                    	</div>
                    	<div class="col text-end">
                    		<div class="btn btn-sm btn-primary notes-save">Save</div>
                    		<div class="btn btn-sm btn-outline-secondary notes-close">Cancel</div>
                    	</div>
                      </div>
                    </div>`;

            html += '</div>';

            html += '</div>';

            return html;
        },
        hideConcatenatedValuePopover() {
            if (this.ConcatenatedValuePopover) {
                PopoverService.hide(this.ConcatenatedValuePopover);
                this.ConcatenatedValuePopover = null;
            }
        },
        showConcatenatedValuePopover(event: any, fieldName: string, fieldFriendlyName: string) {
            const triggerEl = (event?.currentTarget || event?.target) as HTMLElement | null;
            if (!triggerEl) {
                return;
            }

            if (this.ConcatenatedValuePopover) {
                PopoverService.hide(this.ConcatenatedValuePopover);
                this.ConcatenatedValuePopover = null;
            }

            // Popover
            const options = {
                html: true,
                sanitize: false,
                content: this.generateConcatenatedValueHTML(fieldName, fieldFriendlyName),
                placement: 'left',
                customClass: 'concatenated-value-popover',
            };

            this.ConcatenatedValuePopover = PopoverService.initialize(triggerEl, options);
            PopoverService.open(this.ConcatenatedValuePopover);
            const el = PopoverService.getPopoverElement(this.ConcatenatedValuePopover);
            if (!el) {
                return;
            }

            // On open listeners
            PopoverService.onOpen(this.ConcatenatedValuePopover, async () => {
                BootstrapHelper.tooltipInitialize();

                // Close button
                const closeBtn = el.getElementsByClassName('concatenated-close')[0] as HTMLElement | undefined;
                if (!closeBtn) {
                    return;
                }

                closeBtn.addEventListener('click', () => {
                    PopoverService.hide(this.ConcatenatedValuePopover);
                    this.ConcatenatedValuePopover = null;
                });
            });
        },
        generateConcatenatedValueHTML(fieldName: string, friendlyFieldName: string) {
            let html = `<div class="notes-container">`;

            // Header
            html += `<div class="pop-header mb-2">
                            <div class="fw-bold">${friendlyFieldName}</div>
                       </div>`;

            // Body
            html += `<div class="pop-body">`;

            const childFields = this.getConcatenatedChildFieldValues(fieldName);
            if (childFields.length === 0) {
                html += `<div class="row"><div class="col text-muted">No fields found.</div></div>`;
            } else {
                const baseFieldName = GridColumnUtils.getNormalizedFieldName(fieldName).baseFieldname.replace(/_FLATTENED$/, '');
                html += `<table class="w-100">`;
                if (baseFieldName === 'SIT_FEAT_ENCROACH_DTL') {
                    const encroachmentField = childFields.find((childField) => childField.fieldName === 'PU_SITE_FEAT_ENCROACH');
                    const borderingField = childFields.find((childField) =>
                        (childField.fieldName === 'PU_SITE_FEAT_ENCROACH_BRD_ON_SUB' ||
                            childField.fieldName === 'PU_SITE_FEAT_ENCROACH_BRD_ON_ADJ') &&
                        String(childField.value || '').trim() !== ''
                    );

                    if (encroachmentField) {
                        html += `   <tr class="border-bottom">
                                        <td>${encroachmentField.friendlyFieldName}</td>
                                        <td class="text-start">${encroachmentField.value}</td>
                                    </tr>`;
                    }
                    if (borderingField) {
                        html += `   <tr class="border-bottom">
                                        <td colspan="2" class="text-start w-100">${borderingField.value}</td>
                                    </tr>`;
                    }
                } else {
                    for (const childField of childFields) {
                        html += `   <tr class="border-bottom">
                                        <td class="">${childField.friendlyFieldName}</td>
                                        <td class="text-start">${childField.value}</td>
                                    </tr>`;
                    }
                }
                html += `</table>`;
            }

            // Footer
            html += `<div class="container p-0">
                      <div class="row pt-1">
                      	<div class="col align-self-center italic p-color-8 text-small">Individual field values will be saved to report.</div>
                      </div>
                    </div>`;

            html += '</div>';

            html += '</div>';

            return html;
        },
        getConcatenatedChildFieldValues(fieldName: string): Array<{ fieldName: string; friendlyFieldName: string; value: string }> {
            if (this.isSubjectProperty) {
                return this.getSubjectConcatenatedChildFieldValues(fieldName);
            }

            const normalizedField = GridColumnUtils.getNormalizedFieldName(fieldName);
            const childFieldNames = this.getVisibleConcatenatedChildFieldNames(fieldName);
            if (childFieldNames.length === 0) {
                return [];
            }

            // Children are always looked up from the same source dataset as the parent's own
            // sourceInitial (db/mls/se/pr/ff) - never a different comp or a different source.
            const sourceLookup = this.getActiveCompSourceLookupByInitial(this.cellDataClone.sourceInitial);
            const formFields = (formFieldsStore as any)?.FormFields || [];
            const fieldsAdditionalDetails = formFieldsStoreV36.FormFieldsAdditionalDetails as IFormFieldsAdditionalDetails | null;

            return childFieldNames.map((childFieldName: string) => {
                const sourceFieldName = normalizedField.fieldnameIndex
                    ? `${normalizedField.fieldnameIndex}-${childFieldName}`
                    : childFieldName;

                const friendlyName = this.getConcatenatedChildFriendlyName(sourceFieldName, childFieldName, formFields, fieldsAdditionalDetails);
                // Collections with only a single entry are never index-prefixed at the raw
                // feed level (only expandTopLevelCollectionFieldsByPayload-style prefixing for
                // 2+ entries adds it), so also try the bare child field name.
                const value = sourceLookup[sourceFieldName] ?? sourceLookup[childFieldName] ?? '';

                return {
                    fieldName: childFieldName,
                    friendlyFieldName: friendlyName,
                    value: String(value || ''),
                };
            });
        },
        // Subject values live under the Subject's own field-name convention rather than the
        // Comp's, so the parent field must first be re-mapped via SubjectCompRelationship, and
        // its children looked up via ParentChildFields keyed off that Subject field name.
        getSubjectParentChildFields(fieldName: string): string[] {
            const normalizedField = GridColumnUtils.getNormalizedFieldName(fieldName);
            const baseFieldName = normalizedField.baseFieldname.replace(/_FLATTENED$/, '');
            // BATHS is a one-off: DataGrid.vue's getBATHSValue concatenates the Subject's own
            // TOT_BATHS_FULL/TOT_BATHS_HALF directly rather than going through SubjectCompRelationship.
            if (baseFieldName === 'BATHS') {
                return ['TOT_BATHS_FULL', 'TOT_BATHS_HALF'];
            }

            const relationships = (formFieldsStoreV36.SubjectCompRelationship ?? {}) as IFieldSubjectCompRelationship;
            const subjectFieldMappings = relationships[fieldName] ?? relationships[baseFieldName];
            const subjectParentFieldName = subjectFieldMappings?.[0]?.fieldName || baseFieldName;
            return GridColumnUtils.getParentChildFields(subjectParentFieldName);
        },
        getSubjectConcatenatedChildFieldValues(fieldName: string): Array<{ fieldName: string; friendlyFieldName: string; value: string }> {
            const normalizedField = GridColumnUtils.getNormalizedFieldName(fieldName);
            const childFieldNames = this.getSubjectParentChildFields(fieldName);
            if (childFieldNames.length === 0) {
                return [];
            }

            const formFields = (formFieldsStore as any)?.FormFields || [];
            const fieldsAdditionalDetails = formFieldsStoreV36.FormFieldsAdditionalDetails as IFormFieldsAdditionalDetails | null;
            const subjectSourceData = this.getSubjectCompSourceData();

            return childFieldNames.map((childFieldName: string) => {
                const sourceFieldName = normalizedField.fieldnameIndex
                    ? `${normalizedField.fieldnameIndex}-${childFieldName}`
                    : childFieldName;

                const friendlyName = this.getConcatenatedChildFriendlyName(sourceFieldName, childFieldName, formFields, fieldsAdditionalDetails);
                const subjectMatch = subjectSourceData.find((item: any) => String(item?.fieldName || '') === sourceFieldName)
                    ?? subjectSourceData.find((item: any) => String(item?.fieldName || '') === childFieldName);

                return {
                    fieldName: childFieldName,
                    friendlyFieldName: friendlyName,
                    value: String(subjectMatch?.value || ''),
                };
            });
        },
        getConcatenatedChildFriendlyName(sourceFieldName: string, childFieldName: string, formFields: any[], fieldsAdditionalDetails: IFormFieldsAdditionalDetails | null): string {
            const indexedAdditionalDetail = Array.isArray(fieldsAdditionalDetails)
                ? fieldsAdditionalDetails.find((field: any) => String(field?.fieldName || '') === sourceFieldName)
                : null;
            const baseAdditionalDetail = Array.isArray(fieldsAdditionalDetails)
                ? fieldsAdditionalDetails.find((field: any) => String(field?.fieldName || '') === childFieldName)
                : null;
            const indexedFieldData = formFields.find((field: any) => String(field?.fieldName || '') === sourceFieldName);
            const baseFieldData = formFields.find((field: any) => String(field?.fieldName || '') === childFieldName);
            return String(
                indexedAdditionalDetail?.friendlyFieldName ||
                baseAdditionalDetail?.friendlyFieldName ||
                indexedFieldData?.friendlyFieldName ||
                baseFieldData?.friendlyFieldName ||
                childFieldName
            );
        },
        getSubjectCompSourceData(): any[] {
            const comps = computed(() => store.getters[Getters.CompAddressesV36.Comps]).value as any[];
            const subjectComp = comps?.find(
                (comp: any) => comp?.isSubject || String(comp?.compType || '').toLowerCase() === 'subject' || comp?.compNumber === 0
            );
            return Array.isArray(subjectComp?.sourceData) ? subjectComp.sourceData : [];
        },
        getActiveCompSourceLookupByInitial(sourceInitial: string): Record<string, any> {
            const activeComp = computed(() => store.getters[Getters.CompAddressesV36.ActiveComp]).value;
            const normalizedInitial = String(sourceInitial || '').toLowerCase();

            if (normalizedInitial === SourceDataInitials.Database) {
                return activeComp?.sourceDataDB?.sourceData || {};
            }

            if (normalizedInitial === SourceDataInitials.Mls) {
                return activeComp?.sourceDataMLS?.sourceData || {};
            }

            if (normalizedInitial === SourceDataInitials.SmartExchange) {
                return activeComp?.sourceDataSE?.sourceData || {};
            }

            if (normalizedInitial === SourceDataInitials.PublicRecords) {
                return activeComp?.sourceDataPR?.sourceData || {};
            }

            if (normalizedInitial === SourceDataInitials.FormFiller) {
                return activeComp?.sourceDataFF?.sourceData || {};
            }

            return {};
        },
        escapeHtml(value: string): string {
            return String(value || '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },
    },
});
